import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { API, API_BASEURL, PERMISSIONS, SWR_CONFIG, buildUrl } from "../../config";
import { recCheckmarkPopup } from "../../config/recoil/atoms";
import { usePermissions } from "../../hooks";
import useGetSWR from "../../hooks/useGetSWR";
import { CheckmarkIcon, CheckmarkOutlineIcon } from "../../icons";
import { iCheckmarkRes } from "../../types";
import { NOTIFY_CHECKMARK_SAVED, fetcherPut, notificationToShow, openNotification } from "../../utils";
import { getToken } from "../../utils/auth";

interface iProps {
    person_id: string;
    checkmarkFor: "client" | "lead";
    autocomplete?: boolean;
    small?: boolean;
}

export const Checkmark = ({ person_id, checkmarkFor, autocomplete = false, small = false }: iProps) => {
    const { checkPermission } = usePermissions();

    const checkPermissionCheckmark = () => {
        switch (checkmarkFor) {
            case "client":
                return checkPermission(PERMISSIONS.CLIENT_UPDATE_CHECKMARK);
            case "lead":
                return checkPermission(PERMISSIONS.LEAD_UPDATE_CHECKMARK);
        }
    };
    const checkPermissionUncheckmark = () => {
        switch (checkmarkFor) {
            case "client":
                return checkPermission(PERMISSIONS.CLIENT_UPDATE_UNCHECKMARK);
            case "lead":
                return checkPermission(PERMISSIONS.LEAD_UPDATE_UNCHECKMARK);
        }
    };

    const auth0 = useAuth0();

    const [isCheckmarked, setCheckmark] = useState(false);

    const [checkmarkPopup, setCheckmarkState] = useRecoilState(recCheckmarkPopup);

    const isClient = checkmarkFor === "client";

    const PATH = isClient ? API.CLIENT_CHECKMARK : API.LEAD_CHECKMARK; // Checkmark for client or lead

    const url = buildUrl(PATH, { id: person_id }, API_BASEURL); // build url to fetch forms

    const { data: checkmarkRes } = useGetSWR<iCheckmarkRes>(url, SWR_CONFIG.RED_FLAG); // Get Checkmark data

    useEffect(() => {
        if (checkmarkRes) {
            const { client, lead } = checkmarkRes;
            // Depending to which api the call was made to. Only one will be true
            if (client) setCheckmark(client.checkmark);
            if (lead) setCheckmark(lead.checkmark);
        }
    }, [checkmarkRes]);

    useEffect(() => {
        let mounted = true;

        const updateCheckmark = async () => {
            const PUT_PATH = isClient ? API.PUT_CLIENT_CHECKMARK : API.PUT_LEAD_CHECKMARK;
            const putUrl = buildUrl(PUT_PATH, {}, API_BASEURL); // build update url
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [data, error] = await fetcherPut(putUrl, await getToken(auth0), {
                person_id,
                checkmark: !isCheckmarked,
                note: checkmarkPopup.note,
            });

            if (mounted) {
                console.log(error);
                if (error?.code === 403) {
                    openNotification({
                        title: "Forbidden",
                        message: error?.error || "",
                        type: "warning",
                    });
                } else {
                    notificationToShow(error, NOTIFY_CHECKMARK_SAVED);
                }
            }
            if (mounted && !error) setCheckmark(!isCheckmarked);
            if (mounted)
                setCheckmarkState({ isLoading: false, showPopup: false, answered: false, note: "" });
        };

        if (checkmarkPopup.answered) updateCheckmark();

        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkmarkPopup]);

    if (isCheckmarked) {
        return (
            <CheckmarkIcon
                onClick={() =>
                    !autocomplete &&
                    checkPermissionUncheckmark() &&
                    setCheckmarkState({ ...checkmarkPopup, showPopup: true })
                }
                className={`fill-current text-color14 ${small ? "h-4" : "h-[20px]"} ${
                    !autocomplete && checkPermissionUncheckmark() && "cursor-pointer"
                }`}
            />
        );
    }

    return (
        <CheckmarkOutlineIcon
            onClick={() =>
                !autocomplete &&
                checkPermissionCheckmark() &&
                setCheckmarkState({ ...checkmarkPopup, showPopup: true })
            }
            className={`fill-current ${small ? "h-4" : "h-[20px]"} ${
                !autocomplete && checkPermissionCheckmark() && "cursor-pointer"
            }`}
        />
    );
};
